/**
 * Account-level experience context (Wave D follow-up).
 *
 * `account_businesses.experience` is a PER-BUSINESS setting, but some portal
 * surfaces are account-wide (the left nav, list pages). The rule for those:
 *
 *   every business the USER can see is retail  → 'retail'      (hide
 *                                                 hospitality-only surfaces)
 *   every business is hospitality              → 'hospitality' (unchanged)
 *   both kinds                                 → 'mixed'       (show the
 *                                                 union — a mixed account's
 *                                                 restaurant still needs its
 *                                                 floor plans)
 *   not yet loaded / failed                    → null          (fail OPEN:
 *                                                 callers must treat null
 *                                                 like 'mixed')
 *
 * Scope note: /business/list is already filtered to the businesses the
 * logged-in staff member may access, so a staffer who only manages the shop
 * of a mixed account correctly gets the retail portal.
 *
 * One fetch per authenticated owner (module-level cache), shared by every
 * caller. Session changes invalidate in-flight work so a slow response from a
 * previous login can never populate the next user's navigation.
 */

import {
  accountExperience,
  accountExperienceCacheRevision,
  buildAccountExperienceOwnerKey,
  businessExperiences,
  ensureAccountExperienceOwner,
  loadAccountExperience,
  resetAccountExperienceCache,
} from '@/utils/accountExperienceCache'

export function useAccountExperience() {
  const userStore = useUserStore()
  const ownerKey = computed(() =>
    buildAccountExperienceOwnerKey(userStore.profile)
  )

  const loadForOwner = owner =>
    loadAccountExperience(
      owner,
      () => useApi('/business/list', {}, {}, 2)
    )

  if (import.meta.client) {
    watch(
      [ownerKey, accountExperienceCacheRevision],
      ([owner]) => {
        ensureAccountExperienceOwner(owner)
        if (owner) void loadForOwner(owner)
      },
      { immediate: true }
    )
  }

  /** Drop and reload after changing a business's experience. */
  function refreshAccountExperience() {
    const owner = ownerKey.value
    resetAccountExperienceCache(owner)
    if (!import.meta.client || !owner) return Promise.resolve()
    return loadForOwner(owner)
  }

  const isAllRetail = computed(() => accountExperience.value === 'retail')

  /** Per-business lookup for per-venue pages. null while loading/unknown. */
  const businessExperienceOf = (businessId) =>
    businessExperiences.value.get(Number(businessId)) ?? null

  return { accountExperience, isAllRetail, businessExperienceOf, refreshAccountExperience }
}
